import ApiClient from '../../client';
import Vue from 'vue';
import i18n, { DEFAULT_LOCALE } from '@/i18n';
import { buildBrandTokens } from '@/helpers/brand-theme';

const localeByLanguageId = {
  1: 'en',
  2: 'nb'
};

export const state = () => {
  return {
    eventId: null,
    eventDetails: null,
    locale: null,

    // When this is false, we only have the publicly available event info,
    // when it is true, we are most likely logged in and has populated eventDetails with the result from "loadFullEventInfo"
    hasFullEventDetails: false
  };
};

// Drops entries the admin never configured so they can't shadow the
// stylesheet defaults. Also filters the literal 'null' / 'undefined'
// strings older payloads and admin imports have been seen to carry.
const omitEmpty = (styles) =>
  Object.keys(styles).reduce((acc, key) => {
    const value = styles[key];
    if (value === null || value === undefined) return acc;

    const text = String(value).trim();
    if (!text || text === 'null' || text === 'undefined') return acc;

    acc[key] = value;
    return acc;
  }, {});

export const getters = {
  getEventSlug: (state) => state.eventDetails?.eventSlug,
  getTitle: (state) => state.eventDetails.eventName,
  getTwoFact: (state) => state.eventDetails.twoFactor,
  getEvent: (state) => state.eventDetails,
  // Event IANA timezone + display mode (AgendaTimezoneMode: 1 = Dynamic/viewer-local,
  // 2 = Static/event zone). Already delivered on the public + full event-info payloads; the
  // AI-matching views use these to render times via helpers/event-time.js.
  getTimezone: (state) => state.eventDetails?.timezone,
  getTimezoneMode: (state) => state.eventDetails?.agendaTimezoneMode,
  getId: (state) => state.eventId,
  getIdFromEventDetails: (state) => state.eventDetails.eventId,
  getFaqCategoryId: (state) => state.eventDetails.liveFAQCategory,
  isLobbyEnabled: (state) => !!parseInt(state.eventDetails?.liveLobby),
  isProfileToggleVisible: (state) =>
    !!parseInt(state.eventDetails?.profileVisibilities),
  isStageEnabled: (state) => !!parseInt(state.eventDetails?.liveStage),
  isWebappEnabled: (state) => !!parseInt(state.eventDetails?.liveWebApp),
  lobbyElements: (state) => state.eventDetails.lobbyElements,
  hasFullEventDetails: (state) => state.hasFullEventDetails,
  eventDetails: (state) => state.eventDetails,
  features: (state) => {
    // Defaults whenever the full event details have not loaded, and alsop if we are
    // running in devmode with the flag "VUE_APP_ENABLE_ALL_FEATURES" on
    if (process.env.VUE_APP_ENABLE_ALL_FEATURES) {
      return {
        lobbyEnabled: true,
        lobbyChat: true,
        lobbyParticipantList: true,
        lobbyParticipantListShowOnline: true,
        lobbyProfilePage: true,
        lobbyFAQPages: true,
        lobbyAgenda: true,
        lobbySpeakers: true,
        lobbyDates: true,
        lobbySponsors: true,
        lobbySponsorToplist: true,
        sponsorInfo: true,
        sponsorContact: true,
        sponsorUsers: true,
        sponsorPolls: true,
        sponsorWherebyRooms: true,
        stageTabTimeline: true,
        stageTabAgenda: true,
        stageTabSpeakers: true,
        stageTabInfoPages: true,
        stageTabDocuments: true,
        stageTabCustom: true,
        stageTabParticipants: true,
        stageTabParticipantsShowOnline: true,
        stageTabProfile: true,
        stageTabSelfie: true,
        stageFuncTimelinePosting: true,
        stageFuncTimelineCommenting: true,
        stageFuncChatPosting: true,
        sponsorAgendaTab: true,
        sponsorDocumentsTab: true,
        profileEmailVisible: true,
        profilePhoneVisible: true,
        isMultipleVoucher: true,
        isDynamicVoucher: true,
        isCoregoEnabled: true,
        bookingsEnabled: true,
        sponsorFilter: true,
        speakerFilter: true,
        aiMatchingEnabled: true,
        pushNotificationEnabled: true
      };
    }

    return {
      aiMatchingEnabled: false,
      ...state.eventDetails.features,
      lobbyEnabled: !!parseInt(state.eventDetails.liveLobby)
    };
  },

  defaultEmailAndPhoneVisibility: (state) => {
    const visibility = state.eventDetails?.profileVisibilities?.[0] ?? 0;

    return visibility === 2 ||
      (state.eventDetails?.profileVisibilities?.length ?? 0) === 0
      ? false
      : true;
  },

  defaultProfileVisibility: (state) => {
    const visibility = state.eventDetails?.profileVisibilities?.[0] ?? 0;

    return visibility === 2 ||
      (state.eventDetails?.profileVisibilities?.length ?? 0) === 0
      ? false
      : true;
  },

  // Three-state mode driving how the platform exposes the participant
  // list to attendees. Mirrors the encoding the rest of the codebase
  // uses (`defaultProfileVisibility` keys off the same field):
  //   'visible' — every attendee appears in the list by default.
  //               Backend value: profileVisibilities[0] is 0 or 1.
  //   'limited' — the list is shown, but each attendee is hidden by
  //               default; only those who opt-in via their own profile
  //               settings appear.  Backend value: 2.
  //   'hidden'  — the participant list is not shown at all anywhere
  //               (lobby modal, live-stream Participants tab,
  //               JoinCommunity stack).  Backend value: empty array.
  participantVisibilityMode: (state) => {
    const arr = state.eventDetails?.profileVisibilities;
    if (!Array.isArray(arr) || arr.length === 0) return 'hidden';
    return arr[0] === 2 ? 'limited' : 'visible';
  },
  isParticipantListEnabled: (state, getters) => {
    return getters.participantVisibilityMode !== 'hidden';
  },

  isSingleDayEvent: (state) => {
    if (!state.eventDetails) return false;

    const start = Vue.moment(state.eventDetails.startDate);
    const end = Vue.moment(state.eventDetails.endDate);

    return start.isSame(end, 'day');
  },
  isSameMonthEvent: (state) => {
    if (!state.eventDetails) return false;

    const start = Vue.moment(state.eventDetails.startDate);
    const end = Vue.moment(state.eventDetails.endDate);

    return start.isSame(end, 'month');
  },
  isSameYearEvent: (state) => {
    if (!state.eventDetails) return false;

    const start = Vue.moment(state.eventDetails.startDate);
    const end = Vue.moment(state.eventDetails.endDate);

    return start.isSame(end, 'year');
  },
  isWithoutEndDate: (state) => {
    if (!state.eventDetails) return false;

    return !state.eventDetails.endDate;
  },
  getLocale: (state) => state.locale,

  // Raw branding block as returned by the live-event API, or null.
  // `brand-theme.js` consumes this to publish the `--brand-*` layer.
  getBranding: (state) => state.eventDetails?.branding ?? null,

  getDesignStyles: (state) => {
    if (!state.eventDetails || !state.eventDetails.branding) return {};

    const colors = state.eventDetails.branding;

    // Explanation
    // liveBackgroundColor - Bakgrunnsfarge
    // liveTextColorDark - Mørk tekstfarge
    // liveTextColorLight - Lys tekstfarge
    // liveSectionColorOdd - Seksjonsfarge ulik
    // liveSectionColorEven - Seksjonsfarge lik
    // livePrimaryButtonColor - Primær knappefarge
    // liveSecondaryButtonColor - Sekundær knappefarge
    // livePrimaryButtonTextColor - Tekstfarge primærknapp
    // liveSecondaryButtonTextColor - Tekstfarge sekundærknapp
    // liveNavbarBackgroundColor - Navigering bakrunnsfarge

    // Only colours the admin actually configured are emitted. These used
    // to be built with template literals, which turned a missing value
    // into the *string* `"undefined"`; bound inline on `#app` that then
    // shadowed the defaults in `sass/_variables.scss` with a value the
    // browser has to discard, leaving the property unset rather than
    // falling back to the default.
    //
    // The four button variables are deliberately NOT here — they are
    // owned by the `--brand-*` layer on <html> (helpers/brand-theme.js
    // + sass/_variables.scss), which pairs the admin's fill with a
    // label colour it has contrast-checked and supplies the dark-theme
    // variants. An inline copy on `#app` would beat both, because
    // `#app` is a descendant of <body> and its own inline declaration
    // wins for its entire subtree. Web components need them as an
    // object rather than by inheritance — see getWebComponentStyles.
    return omitEmpty({
      '--background-image': colors.liveBackgroundImage
        ? `url("${colors.liveBackgroundImage}")`
        : null,
      '--background-color': colors.liveBackgroundColor,
      '--navbar-background-color': colors.liveNavbarBackgroundColor,

      '--section-odd': colors.liveSectionColorOdd,
      '--section-even': colors.liveSectionColorEven,

      '--color-text': colors.liveTextColorDark,

      // Login
      '--login-background-color': colors.loginPageBackgroundColor,
      '--lobby-color-text': colors.liveLobbyTextColor,

      '--login-color-text': colors.loginPageTextColor
    });
  },

  // getDesignStyles plus the button variables, for the `<tappin-*>` web
  // components. Their styles live in a shadow root and are handed the
  // map as a JS property, so unlike the Vue tree they cannot inherit
  // these from <html> and need them passed explicitly.
  getWebComponentStyles: (state, getters) => {
    const colors = state.eventDetails?.branding;
    if (!colors) return getters.getDesignStyles;

    // Same contrast-safe fill and label the rest of the platform uses, so
    // lobby web-component buttons match.
    const brand = buildBrandTokens(colors) || {};

    return {
      ...getters.getDesignStyles,
      ...omitEmpty({
        '--color-primary-bg':
          brand['--brand-primary'] || colors.livePrimaryButtonColor,
        '--color-primary-text':
          brand['--brand-on-primary'] || colors.livePrimaryButtonTextColor,
        '--color-secondary-bg': colors.liveSecondaryButtonColor,
        '--color-secondary-text': colors.liveSecondaryButtonTextColor
      })
    };
  }
};

export const mutations = {
  setEventId: (state, eventId) => {
    state.eventId = eventId;
  },
  setEventDetails: (state, eventDetails) => {
    state.eventDetails = eventDetails;
  },
  clearEventDetails: (state) => {
    state.eventDetails = null;
  },
  setHasFullEventDetails: (state, hasFullInfo) => {
    state.hasFullEventDetails = hasFullInfo;
  },
  setLocale: (state, payload) => {
    state.locale = payload;
  }
};

export const actions = {
  async loadAndCacheEventDetails({ state, commit, dispatch }, eventId) {
    const response = await ApiClient.getPublicEventInfo(eventId);
    if (response.data) {
      commit('setEventId', response.data.eventId);
      dispatch('changeFavicon', response.data.faviconUrl);
      dispatch('changeTitle', response.data.eventName);
      dispatch('changeLanguage', response.data.languageId);
      commit('setEventDetails', response.data);
      commit('setHasFullEventDetails', false);
    }
  },
  async loadFullEventInfo({ state, commit, dispatch }) {
    const eventId = state.eventId;
    commit('setEventId', eventId);
    const response =
      await ApiClient.getFullEventInfoForEventCurrentlyLoggedInTo();
    if (response.data) {
      dispatch('changeLanguage', response.data.languageId);
      commit('setEventDetails', response.data);
      commit('setHasFullEventDetails', true);
    }
  },

  updateEventInfo({ state, commit, dispatch }, data) {
    commit('setEventDetails', data);
  },

  async changeFavicon({ state }, icon) {
    if (icon) {
      console.log('Setting favicon: ', icon);
      const favicons = document.getElementsByClassName('favicon');

      for (var i = 0; i < favicons.length; i++) {
        favicons[i].href = `${icon}?size=${icon.sizes ?? '256x256'}`;
      }
    }
  },

  async changeTitle({ state }, title) {
    if (title) {
      document.title = title;
    }
  },

  // `language` is the event's languageId from the API: "1" = English (en-US), "2" = Norwegian.
  // An unrecognised or missing value keeps the current locale (English on first load) rather
  // than leaving i18n with a null locale.
  async changeLanguage({ state, commit }, language) {
    const locale =
      localeByLanguageId[String(language ?? '').trim()] ||
      state.locale ||
      DEFAULT_LOCALE;

    if (locale !== state.locale) {
      commit('setLocale', locale);
    }

    i18n.locale = locale;
    Vue.moment.locale(locale);
    document.documentElement.setAttribute('lang', locale);
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
